import { apiConnection } from "@/services/api-connection";
import { fileManager } from "@/services/file/file-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { mainStore, trainingCourseStore, triadStore } from "@/main";
import { useTriadStore } from "@/store/triad/triad";
import { messengerMessageManager } from "@/services/messenger-message/messenger-message-manager";

class SkillManager {
  SKILL_CONFIG_FROM = 0;
  SKILL_REFERENTIAL_FORM = 1;
  SKILL_SESSION_FORM = 2;

  SKILL_LEVELS = ["not_acquired", "being_acquired", "acquired", "good_mastery", "excellent_mastery"];
  getPrefixURL = () => {
    return "/app/skill/" + trainingCourseStore.trainingCourse.id + "/" + triadStore.triad.id;
  };

  postSkills = (skills, currentSessionId) => {
    return apiConnection
      .post(
        this.getPrefixURL() + "/save/" + currentSessionId,
        {
          skills: skills,
        },
        true,
      )
      .then((response) => response);
  };

  getTriadSkills = () => {
    return apiConnection.get(this.getPrefixURL() + "/list", {}).then((response) => response);
  };

  downloadSkillToPdf = (currentSessionId) => {
    return apiConnection.get(this.getPrefixURL() + "/export/" + currentSessionId, {}).then((res) => {
      messengerMessageManager.processMessage(res);
    });
  };

  downloadSkillToExcel = (currentSessionId) => {
    return apiConnection.post(this.getPrefixURL() + "/export-excel/" + currentSessionId, {}).then((res) => {
      messengerMessageManager.processMessage(res);
    });
  };

  getPdf = () => {
    mainStore.fileDownloadRequest();
    return apiConnection
      .get(
        this.getPrefixURL() + "/export",
        {
          triad: triadStore.triad.id,
          finalEval: 0,
        },
        true,
      )
      .then((success) => {
        if (success.data) {
          fileManager.saveFile(success.fileName, success.data, "application/pdf");
        } else {
          notificationManager.showAlert("error", i18n.global.t("error_file"));
        }
        mainStore.fileDownloadSuccess();
      });
  };

  getJustificativeForm(id = null) {
    return apiConnection
      .get(
        "/app/skill/" +
          trainingCourseStore.trainingCourse.id +
          "/" +
          triadStore.triad.id +
          "/justificative-form" +
          (id ? "/" + id : ""),
      )
      .then((response) => response);
  }

  submitJustificativeForm(id, result) {
    return apiConnection
      .put(
        "/app/skill/" +
          trainingCourseStore.trainingCourse.id +
          "/" +
          triadStore.triad.id +
          "/justificative-post" +
          (id ? "/" + id : ""),
        result,
      )
      .then((response) => response);
  }

  initSession() {
    triadStore.setCurrentSession(
      triadStore.sessions.find(
        (session) => new Date(session.startDate) < Date.now() && new Date(session.endDate) > Date.now(),
      ),
    );
  }

  sessionCharts() {
    const triad = useTriadStore().triad;
    const session = useTriadStore().currentSession;
    return apiConnection
      .get("/app/skill/" + triad.trainingCourse.id + "/" + triad.id + "/charts/" + session.id)
      .then((response) => response);
  }

  getSkillSessionAdminForm() {
    return apiConnection.get("/app/admin/skill/session/form").then((response) => response);
  }

  postSkillSessionAdminForm(sessionsToCreate) {
    return apiConnection.put("/app/admin/skill/session", sessionsToCreate).then((response) => response);
  }

  getSkillConfig(trainingCourse, triad) {
    return apiConnection.get("/app/skill/" + trainingCourse + "/" + triad + "/config").then((response) => response);
  }

  getSkillStatistic(trainingCourseId, triadId) {
    return apiConnection.get("/app/skill/" + trainingCourseId + "/" + triadId + "/statistics");
  }

  updateSkillLevels(maxSkillLevel, skillLevels) {
    const maxLevelValue = maxSkillLevel.vars.value;

    skillLevels.vars.attr["data-min-tag"] = parseInt(maxLevelValue);
    skillLevels.vars.attr["data-max-tag"] = parseInt(maxLevelValue);

    const allLevels = this.SKILL_LEVELS.slice(0, maxLevelValue).map((key) => {
      const label = i18n.global.t(`studea_manager.skill.default.${key}`);

      return {
        value: label,
        data: label,
        label,
      };
    });

    while (skillLevels.vars.value.length > maxLevelValue) {
      skillLevels.vars.value.pop();
      skillLevels.vars.choices.pop();
    }

    while (skillLevels.vars.value.length < maxLevelValue) {
      const nextIndex = skillLevels.vars.value.length;

      if (nextIndex >= allLevels.length) {
        break;
      }

      const newValue = allLevels[nextIndex];

      skillLevels.vars.value.push(newValue);
      skillLevels.vars.choices.push(newValue);
    }
  }
}

export const skillManager = new SkillManager();
